import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Skeleton } from './ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  MessageSquare, Phone, Mail, MessageCircle, Users, Sparkles, MoreHorizontal,
  Pencil, Trash2, Paperclip, X, Plus, Loader2, AlertCircle, CheckCircle2, Clock, CalendarDays, AtSign, Image as ImageIcon, FileText, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime, cn } from '../lib/utils';
import { customerActivityApi, usersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CHANNELS = [
  { value: 'phone_call', label: 'Phone Call', Icon: Phone, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { value: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, color: 'text-green-700 bg-green-50 border-green-200' },
  { value: 'email', label: 'Email', Icon: Mail, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { value: 'text_message', label: 'Text Message', Icon: MessageSquare, color: 'text-sky-600 bg-sky-50 border-sky-200' },
  { value: 'in_person', label: 'In Person', Icon: Users, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { value: 'exhibition', label: 'Exhibition', Icon: Sparkles, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'other', label: 'Other', Icon: MoreHorizontal, color: 'text-slate-600 bg-slate-50 border-slate-200' },
];

const OUTCOMES = [
  { value: 'none', label: 'No follow-up needed' },
  { value: 'pending_followup', label: 'Pending follow-up' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'needs_escalation', label: 'Needs escalation' },
];

const channelMeta = (v) => CHANNELS.find(c => c.value === v) || CHANNELS[6];

const relativeTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 86400 * 365) return `${Math.floor(diff / (86400 * 30))}mo ago`;
  return `${Math.floor(diff / (86400 * 365))}y ago`;
};

const ChannelBadge = ({ channel, isAuto }) => {
  const { Icon, label, color } = channelMeta(channel);
  return (
    <Badge variant="outline" className={cn('text-[10px] font-medium', color)} data-testid={`channel-badge-${channel}`}>
      <Icon className="h-3 w-3 mr-1" />
      {label}
      {isAuto && <span className="ml-1 opacity-60">· auto</span>}
    </Badge>
  );
};

// ─── Log / Edit Activity Dialog ─────────────────────────────────────────────
const ActivityDialog = ({ open, onOpenChange, customerId, contacts, teamUsers, editing, onSaved }) => {
  const isEdit = !!editing;
  const [channel, setChannel] = useState('phone_call');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [contactKey, setContactKey] = useState('');
  const [activityDate, setActivityDate] = useState('');
  const [outcome, setOutcome] = useState('none');
  const [nextActionDate, setNextActionDate] = useState('');
  const [mentions, setMentions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activityId, setActivityId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Build phone -> contact map and dropdown options
  const contactOptions = useMemo(() => {
    return (contacts || []).map((c, i) => ({
      key: `${c.phone || c.email || c.name || ''}::${i}`,
      ...c,
    }));
  }, [contacts]);

  useEffect(() => {
    if (!open) return;
    if (isEdit && editing) {
      setChannel(editing.channel);
      setSubject(editing.subject);
      setNotes(editing.notes || '');
      const ctc = editing.contact || {};
      const key = contactOptions.find(o => (o.phone && o.phone === ctc.phone) || (o.email && o.email === ctc.email));
      setContactKey(key ? key.key : '__manual__');
      setActivityDate((editing.activity_date || '').slice(0, 16));
      setOutcome(editing.outcome || 'none');
      setNextActionDate(editing.next_action_date || '');
      setMentions(editing.mentions || []);
      setActivityId(editing.id);
      setAttachments(editing.attachments || []);
    } else {
      setChannel('phone_call');
      setSubject('');
      setNotes('');
      setContactKey(contactOptions[0]?.key || '__manual__');
      // default to local now in datetime-local format
      const d = new Date();
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      setActivityDate(d.toISOString().slice(0, 16));
      setOutcome('none');
      setNextActionDate('');
      setMentions([]);
      setActivityId(null);
      setAttachments([]);
    }
  }, [open, isEdit, editing, contactOptions]);

  const selectedContact = useMemo(() => {
    if (contactKey === '__manual__') return null;
    return contactOptions.find(o => o.key === contactKey) || null;
  }, [contactKey, contactOptions]);

  const submit = async () => {
    if (!subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    setSaving(true);
    const body = {
      channel,
      subject: subject.trim(),
      notes: notes.trim(),
      contact: selectedContact
        ? { name: selectedContact.name, phone: selectedContact.phone, email: selectedContact.email, designation: selectedContact.designation, source: selectedContact.source }
        : { source: 'manual' },
      activity_date: activityDate ? new Date(activityDate).toISOString() : null,
      outcome,
      next_action_date: outcome === 'pending_followup' ? (nextActionDate || null) : null,
      mentions,
    };
    try {
      let saved;
      if (isEdit) {
        const r = await customerActivityApi.update(customerId, activityId, body);
        saved = r.data;
      } else {
        const r = await customerActivityApi.create(customerId, body);
        saved = r.data;
        setActivityId(saved.id);
      }
      toast.success(isEdit ? 'Activity updated' : 'Activity logged');
      onSaved?.(saved);
      // For new entries: keep dialog open if user is uploading attachments; close otherwise
      if (!isEdit && attachments.length === 0) {
        // give user a chance to upload — pause closure if they click attach
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onUploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!activityId) {
      toast.error('Save the activity first, then attach files');
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const r = await customerActivityApi.uploadAttachment(customerId, activityId, file);
        setAttachments(prev => [...prev, r.data]);
      }
      toast.success(`${files.length} file${files.length === 1 ? '' : 's'} attached`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const onRemoveAttachment = async (att) => {
    if (!window.confirm(`Remove "${att.name}"?`)) return;
    try {
      await customerActivityApi.deleteAttachment(customerId, activityId, att.id);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
      toast.success('Removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    }
  };

  const toggleMention = (uid) => {
    setMentions(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="activity-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit activity' : 'Log new activity'}</DialogTitle>
          <DialogDescription>
            Record this touchpoint with the customer. Use the same form for calls, meetings, exhibitions, and any other communication.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Channel + Contact row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Channel *</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="activity-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHANNELS.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="flex items-center gap-2"><c.Icon className="h-3.5 w-3.5" /> {c.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact person</Label>
              <Select value={contactKey} onValueChange={setContactKey}>
                <SelectTrigger data-testid="activity-contact-select"><SelectValue placeholder="Pick contact…" /></SelectTrigger>
                <SelectContent>
                  {contactOptions.map(c => (
                    <SelectItem key={c.key} value={c.key}>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium">{c.name || '—'}</span>
                        <span className="text-[10px] text-muted-foreground">{c.phone || c.email || c.source}</span>
                      </span>
                    </SelectItem>
                  ))}
                  <SelectItem value="__manual__">Not in list / unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subject */}
          <div>
            <Label>Subject *</Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="e.g., Discussed October order timeline"
              maxLength={200}
              data-testid="activity-subject"
            />
          </div>

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              maxLength={5000}
              placeholder="Add any details, agreements, action items, or context…"
              data-testid="activity-notes"
            />
          </div>

          {/* Date + Outcome */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>When did this happen?</Label>
              <Input
                type="datetime-local"
                value={activityDate}
                onChange={e => setActivityDate(e.target.value)}
                data-testid="activity-date"
              />
            </div>
            <div>
              <Label>Outcome</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger data-testid="activity-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OUTCOMES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Follow-up date */}
          {outcome === 'pending_followup' && (
            <div>
              <Label>Next action date</Label>
              <Input
                type="date"
                value={nextActionDate}
                onChange={e => setNextActionDate(e.target.value)}
                data-testid="activity-next-action-date"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                You'll see this on your dashboard follow-ups list and as a reminder badge on the customer card.
              </p>
            </div>
          )}

          {/* Mentions */}
          <div>
            <Label className="flex items-center gap-1"><AtSign className="h-3.5 w-3.5" /> Mention teammates</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(teamUsers || []).map(u => {
                const on = mentions.includes(u.id);
                return (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => toggleMention(u.id)}
                    className={cn(
                      'text-xs px-2 py-1 rounded border transition-colors',
                      on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background hover:bg-muted border-border'
                    )}
                    data-testid={`mention-toggle-${u.id}`}
                  >
                    @{u.name} <span className="opacity-60 ml-1">{u.role}</span>
                  </button>
                );
              })}
            </div>
            {mentions.length > 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Mentioned users get a notification with a link to this activity.
              </p>
            )}
          </div>

          {/* Attachments */}
          <div>
            <Label className="flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> Attachments</Label>
            {!activityId ? (
              <p className="text-[11px] text-muted-foreground">
                Save the activity first to attach files (screenshots, PDFs, images — up to 10).
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1.5">
                  {attachments.map(a => (
                    <div key={a.id} className="border rounded p-2 flex items-center gap-2 text-xs" data-testid={`attachment-${a.id}`}>
                      {a.content_type?.startsWith('image/') ? <ImageIcon className="h-4 w-4 text-blue-500" /> : <FileText className="h-4 w-4 text-slate-500" />}
                      <span className="truncate flex-1" title={a.name}>{a.name}</span>
                      <button onClick={() => onRemoveAttachment(a)} className="text-rose-500 hover:text-rose-700">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <Label className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 border rounded text-xs cursor-pointer hover:bg-muted">
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  Add file
                  <input type="file" hidden multiple onChange={onUploadFile} disabled={uploading} data-testid="activity-file-input" />
                </Label>
                <span className="text-[10px] text-muted-foreground ml-2">Max 10 MB per file</span>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="activity-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? 'Save changes' : 'Log activity'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Activity Row ────────────────────────────────────────────────────────────
const ActivityRow = ({ activity, onEdit, onDelete, onPreviewAttachment }) => {
  const meta = channelMeta(activity.channel);
  const Icon = meta.Icon;
  return (
    <div className="border-l-2 border-slate-200 dark:border-neutral-700 pl-4 pb-4 relative" data-testid={`activity-row-${activity.id}`}>
      <div className={cn('absolute -left-[7px] top-0.5 h-3 w-3 rounded-full border-2 border-background', meta.color.replace('text-', 'bg-').split(' ')[0])} />
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <ChannelBadge channel={activity.channel} isAuto={activity.is_auto} />
            {activity.outcome === 'pending_followup' && (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                <Clock className="h-3 w-3 mr-1" /> Follow-up{activity.next_action_date ? ` · ${activity.next_action_date}` : ''}
              </Badge>
            )}
            {activity.outcome === 'resolved' && (
              <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Resolved
              </Badge>
            )}
            {activity.outcome === 'needs_escalation' && (
              <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-700 bg-rose-50">
                <AlertCircle className="h-3 w-3 mr-1" /> Escalation
              </Badge>
            )}
            <span className="text-[10px] text-muted-foreground" title={activity.activity_date}>
              <CalendarDays className="h-3 w-3 inline mr-0.5" />{formatDateTime(activity.activity_date)}
            </span>
          </div>
          <p className="font-semibold text-sm">{activity.subject}</p>
          {activity.notes && (
            <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-1">{activity.notes}</p>
          )}
          {/* Contact */}
          {activity.contact && (activity.contact.name || activity.contact.phone || activity.contact.email) && (
            <p className="text-[10px] text-muted-foreground mt-1.5">
              with <span className="font-medium text-foreground">{activity.contact.name || '—'}</span>
              {activity.contact.phone && <> · {activity.contact.phone}</>}
              {activity.contact.email && <> · {activity.contact.email}</>}
            </p>
          )}
          {/* Mentions */}
          {(activity.mention_names || []).length > 0 && (
            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
              <AtSign className="h-3 w-3 text-indigo-500" />
              {activity.mention_names.map(m => (
                <Badge key={m.id} variant="outline" className="text-[10px] border-indigo-200 text-indigo-700 bg-indigo-50">{m.name}</Badge>
              ))}
            </div>
          )}
          {/* Attachments */}
          {(activity.attachments || []).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
              {activity.attachments.map(a => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onPreviewAttachment(a)}
                  className="text-xs flex items-center gap-1.5 border rounded px-2 py-1.5 hover:bg-muted transition-colors text-left min-w-0"
                  data-testid={`activity-attachment-${a.id}`}
                >
                  {a.content_type?.startsWith('image/') ? <ImageIcon className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" /> : <FileText className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />}
                  <span className="truncate flex-1">{a.name}</span>
                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-2">
            {activity.is_auto
              ? <span className="italic">Logged automatically · {activity.auto_source?.replace(/_/g, ' ')}</span>
              : <>by <span className="font-medium">{activity.created_by_name}</span> · {relativeTime(activity.created_at)}{activity.edited_at && ' · (edited)'}</>
            }
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {activity.can_edit && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(activity)} data-testid={`activity-edit-${activity.id}`}>
              <Pencil className="h-3 w-3" />
            </Button>
          )}
          {activity.can_delete && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => onDelete(activity)} data-testid={`activity-delete-${activity.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Quick inline note input ────────────────────────────────────────────────
const QuickNoteInput = ({ customerId, onSaved }) => {
  const [channel, setChannel] = useState('phone_call');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      // Use first line as subject (capped), full text as notes
      const firstLine = t.split('\n')[0].slice(0, 200);
      await customerActivityApi.create(customerId, {
        channel,
        subject: firstLine,
        notes: t.length > firstLine.length ? t : '',
        outcome: 'none',
      });
      toast.success('Note added to activity log');
      setText('');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl+Enter posts
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="rounded-lg border border-dashed p-2.5 mb-3 bg-slate-50/50 dark:bg-neutral-900/50" data-testid="quick-note-input">
      <div className="flex items-start gap-2">
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="h-9 w-[130px] text-xs flex-shrink-0" data-testid="quick-note-channel">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHANNELS.map(c => (
              <SelectItem key={c.value} value={c.value}>
                <span className="flex items-center gap-1.5"><c.Icon className="h-3 w-3" /> {c.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1 min-w-0">
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Quick note — e.g. 'Called about pending PO, waiting on gold rate'"
            rows={2}
            className="text-sm resize-none"
            data-testid="quick-note-text"
            disabled={saving}
          />
        </div>
        <Button
          size="sm"
          onClick={submit}
          disabled={saving || !text.trim()}
          className="h-9 flex-shrink-0"
          data-testid="quick-note-submit"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
          Add
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1 pl-1">
        Cmd/Ctrl+Enter to add · For attachments, follow-ups or @mentions use <span className="font-medium">Log activity</span>
      </p>
    </div>
  );
};

// ─── Main Activity Log Section ───────────────────────────────────────────────
const CustomerActivityLog = ({ customerId }) => {
  const { token } = useAuth();
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_channel: {}, last_contact_at: null, open_followups: 0 });
  const [contacts, setContacts] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [channelFilter, setChannelFilter] = useState('all');
  const [previewAtt, setPreviewAtt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        customerActivityApi.list(customerId, { channel: channelFilter === 'all' ? undefined : channelFilter, limit: 200 }),
        customerActivityApi.listContacts(customerId),
      ]);
      setActivities(a.data.activities || []);
      setStats(a.data.stats || {});
      setContacts(c.data.contacts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, [customerId, channelFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    usersApi.getTeam().then(r => setTeam(Array.isArray(r.data) ? r.data : [])).catch(() => setTeam([]));
  }, []);

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete this ${a.channel.replace('_', ' ')} entry "${a.subject}"?`)) return;
    try {
      await customerActivityApi.delete(customerId, a.id);
      toast.success('Activity deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const channelCounts = stats.by_channel || {};

  return (
    <Card data-testid="customer-activity-log">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-indigo-500" /> Activity Log
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Every conversation — calls, WhatsApp, emails, meetings, exhibitions — in one timeline.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {stats.last_contact_at && (
              <Badge variant="outline" className="text-[10px]" data-testid="last-contact-chip">
                Last: {relativeTime(stats.last_contact_at)}
              </Badge>
            )}
            {stats.open_followups > 0 && (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                <Clock className="h-3 w-3 mr-1" /> {stats.open_followups} pending follow-up{stats.open_followups === 1 ? '' : 's'}
              </Badge>
            )}
            <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="log-activity-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Log activity
            </Button>
          </div>
        </div>

        {/* Channel filter chips */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <button
            onClick={() => setChannelFilter('all')}
            className={cn('text-[10px] px-2 py-0.5 rounded border', channelFilter === 'all' ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted')}
            data-testid="channel-filter-all"
          >
            All · {stats.total || 0}
          </button>
          {CHANNELS.map(c => {
            const count = channelCounts[c.value] || 0;
            if (count === 0) return null;
            const on = channelFilter === c.value;
            return (
              <button
                key={c.value}
                onClick={() => setChannelFilter(c.value)}
                className={cn('text-[10px] px-2 py-0.5 rounded border flex items-center gap-1', on ? c.color.replace('bg-', 'bg-').replace('50', '100') : 'bg-background hover:bg-muted')}
                data-testid={`channel-filter-${c.value}`}
              >
                <c.Icon className="h-3 w-3" />
                {c.label} · {count}
              </button>
            );
          })}
        </div>
      </CardHeader>

      <CardContent>
        <QuickNoteInput customerId={customerId} onSaved={load} />
        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
        ) : activities.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p>No activity logged yet for this customer.</p>
            <p className="text-xs mt-1">Click <span className="font-medium">"Log activity"</span> to record your first touchpoint.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {activities.map(a => (
              <ActivityRow
                key={a.id}
                activity={a}
                onEdit={(act) => { setEditing(act); setDialogOpen(true); }}
                onDelete={handleDelete}
                onPreviewAttachment={(att) => setPreviewAtt({ ...att, activityId: a.id })}
              />
            ))}
          </div>
        )}
      </CardContent>

      <ActivityDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) load(); }}
        customerId={customerId}
        contacts={contacts}
        teamUsers={team}
        editing={editing}
        onSaved={() => load()}
      />

      {/* Attachment Preview Lightbox */}
      <Dialog open={!!previewAtt} onOpenChange={(v) => !v && setPreviewAtt(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-sm truncate">{previewAtt?.name}</DialogTitle>
          </DialogHeader>
          {previewAtt && (
            previewAtt.content_type?.startsWith('image/') ? (
              <img
                src={customerActivityApi.attachmentUrl(customerId, previewAtt.activityId, previewAtt.id, token)}
                alt={previewAtt.name}
                className="max-h-[70vh] w-auto mx-auto"
              />
            ) : (
              <div className="text-center py-8 space-y-3">
                <FileText className="h-12 w-12 text-slate-400 mx-auto" />
                <a
                  href={customerActivityApi.attachmentUrl(customerId, previewAtt.activityId, previewAtt.id, token)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 border rounded text-sm hover:bg-muted"
                  data-testid="attachment-download-link"
                >
                  <ExternalLink className="h-4 w-4" /> Open / download "{previewAtt.name}"
                </a>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CustomerActivityLog;
